import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import Button from "@/components/common/Buttons/Button";
import useNavigationHelpers from "@/hooks/useNavigationHelpers";
import { deviceRoleSearchParamsSchema } from "@/routes/-search-params";
import { DeviceRole } from "@/types/device-role";

export const Route = createFileRoute("/$locale/$partner/_app/_card/prepare-scanner-device")({
	validateSearch: (search) => deviceRoleSearchParamsSchema.parse(search),
	component: PrepareScannerDeviceRouteComponent,
});

type PrepareScannerDevicePageProps = {
	deviceRole?: DeviceRole;
};

const scannerPreparationInstructions = [
	{
		key: "torch",
		icon: "/images/camera.svg",
		text: "instructions.Use a device with a torch and a good-quality rear camera.",
	},
	{
		key: "clean-camera-lense",
		icon: "/images/clean-camera-lenses.svg",
		text: "instructions.Make sure the camera lenses are clean.",
	},
	{
		key: "environment-light",
		icon: "/images/light-bulb.svg",
		text: "instructions.Keep your environment with dimmed or natural light and avoid direct glares from other light sources.",
	},
] as const;

function PrepareScannerDeviceRouteComponent() {
	const { "device-role": deviceRole } = Route.useSearch();

	return <PrepareScannerDevicePage deviceRole={deviceRole} />;
}

function PrepareScannerDevicePage({ deviceRole }: PrepareScannerDevicePageProps) {
	const { t } = useTranslation();
	const { goToNextStep, networkAwarePush } = useNavigationHelpers();

	return (
		<div className="h-full w-full flex flex-col text-center items-center pt-10 sm:pt-20">
			<h3 className="whitespace-pre-line pb-8">{t("instructions.Prepare the scanning smartphone")}</h3>
			<div
				className={`flex flex-col h-full justify-around overflow-y-auto ${deviceRole === "scanner" ? "pb-36" : "pb-20"}`}
			>
				<div className="min-h-60 lg:min-h-80 max-h-[60dvh] w-full relative">
					<img
						src={`/images/scanner-device.svg`}
						alt="devices"
						className="w-full h-full object-contain"
					/>
				</div>
				<div className="flex flex-col items-start text-start gap-5 max-w-[600px]">
					{scannerPreparationInstructions.map((instruction) => (
						<div
							key={instruction.key}
							className="flex gap-3"
						>
							<img
								src={instruction.icon}
								alt={instruction.key}
								style={{ width: 32, height: 32 }}
							/>
							<p>{t(instruction.text)}</p>
						</div>
					))}
				</div>
			</div>
			<div className="absolute bottom-0 left-0 bg-fixed-buttons w-full items-center flex flex-col-reverse sm:flex-row flex-wrap justify-center gap-2 lg:gap-4 mt-auto pb-6 sm:pb-10">
				{deviceRole === "scanner" && (
					<Button
						label={t("btn.Change scanner device")}
						intent="quaternary"
						onClick={() => {
							networkAwarePush("pair-scanner-device");
						}}
					/>
				)}
				<Button
					label={t("btn.I am ready!")}
					onClick={() => {
						if (deviceRole !== "scanner") {
							networkAwarePush("pair-scanner-device");
						} else {
							goToNextStep();
						}
					}}
				/>
			</div>
		</div>
	);
}
